/**
 * BioluminescentTheme - Design system for Cells & Life UI
 * Deep-sea bioluminescence meets scientific laboratory aesthetic
 * Ethereal, glowing, organic - like peering into a living microscope
 */

import Phaser from 'phaser';

/**
 * Core color palette - deep ocean with bioluminescent accents
 */
export const COLORS = {
  // Deep backgrounds
  abyss: 0x040810,
  deepOcean: 0x0a1628,
  midnight: 0x0d1f35,
  twilight: 0x132744,

  // Panel colors
  panelBg: 0x0c1a2e,
  panelBorder: 0x1a3a5c,
  panelHighlight: 0x1e4a6f,

  // Bioluminescent glows
  cyanGlow: 0x00d4ff,
  tealGlow: 0x00ffc8,
  greenGlow: 0x00ff88,
  warmGlow: 0xffaa00,
  pinkGlow: 0xff6b9d,
  purpleGlow: 0xa855f7,

  // Functional colors
  success: 0x10b981,
  warning: 0xf59e0b,
  error: 0xef4444,
  info: 0x3b82f6,

  // Text hierarchy
  textPrimary: 0xf0f9ff,
  textSecondary: 0x94d2ff,
  textMuted: 0x5a8cad,
  textDim: 0x3a5a7a,

  // Data colors
  dataRevealed: 0x10b981,
  dataHidden: 0x2a4a6a,
  dataPending: 0x4a7a9a,
} as const;

/**
 * Animation durations
 */
export const TIMING = {
  instant: 100,
  fast: 200,
  normal: 300,
  slow: 500,
  gentle: 800,
  breathe: 2000,
} as const;

/**
 * Create a glowing panel background with organic edges
 */
export function createGlowingPanel(
  scene: Phaser.Scene,
  width: number,
  height: number,
  options: {
    glowColor?: number;
    glowIntensity?: number;
    cornerRadius?: number;
    borderWidth?: number;
    hasInnerGlow?: boolean;
    hasCornerAccents?: boolean;
    hasScanlines?: boolean;
  } = {}
): Phaser.GameObjects.Container {
  const {
    glowColor = COLORS.cyanGlow,
    glowIntensity = 0.3,
    cornerRadius = 12,
    borderWidth = 2,
    hasInnerGlow = true,
    hasCornerAccents = true,
    hasScanlines = false,
  } = options;

  const container = scene.add.container(0, 0);

  // Outer glow (multiple layers for soft effect)
  for (let i = 3; i >= 0; i--) {
    const glowGraphics = scene.add.graphics();
    const spread = (i + 1) * 4;
    const alpha = glowIntensity * (0.15 / (i + 1));
    glowGraphics.fillStyle(glowColor, alpha);
    glowGraphics.fillRoundedRect(-spread, -spread, width + spread * 2, height + spread * 2, cornerRadius + spread);
    container.add(glowGraphics);
  }

  // Main panel shadow
  const shadow = scene.add.graphics();
  shadow.fillStyle(0x000000, 0.5);
  shadow.fillRoundedRect(3, 3, width, height, cornerRadius);
  container.add(shadow);

  // Main panel background with gradient simulation
  const bg = scene.add.graphics();

  // Base fill
  bg.fillStyle(COLORS.panelBg, 0.95);
  bg.fillRoundedRect(0, 0, width, height, cornerRadius);

  // Top gradient highlight
  bg.fillStyle(COLORS.twilight, 0.3);
  bg.fillRoundedRect(0, 0, width, height * 0.4, { tl: cornerRadius, tr: cornerRadius, bl: 0, br: 0 });

  container.add(bg);

  // Inner glow effect
  if (hasInnerGlow) {
    const innerGlow = scene.add.graphics();
    innerGlow.lineStyle(1, glowColor, 0.15);
    innerGlow.strokeRoundedRect(4, 4, width - 8, height - 8, cornerRadius - 2);
    container.add(innerGlow);
  }

  // Panel border
  const border = scene.add.graphics();
  border.lineStyle(borderWidth, COLORS.panelBorder, 0.8);
  border.strokeRoundedRect(0, 0, width, height, cornerRadius);
  container.add(border);

  // Glowing border accent on top
  const glowBorder = scene.add.graphics();
  glowBorder.lineStyle(1, glowColor, 0.5);
  glowBorder.strokeRoundedRect(0, 0, width, height, cornerRadius);
  container.add(glowBorder);

  // Corner accents (bioluminescent dots)
  if (hasCornerAccents) {
    const corners = [
      { x: 8, y: 8 },
      { x: width - 8, y: 8 },
      { x: 8, y: height - 8 },
      { x: width - 8, y: height - 8 },
    ];

    corners.forEach(pos => {
      const dot = scene.add.graphics();
      dot.fillStyle(glowColor, 0.8);
      dot.fillCircle(pos.x, pos.y, 2);
      // Dot glow
      dot.fillStyle(glowColor, 0.2);
      dot.fillCircle(pos.x, pos.y, 5);
      container.add(dot);
    });
  }

  // Scanlines overlay
  if (hasScanlines) {
    const scanlines = scene.add.graphics();
    scanlines.lineStyle(1, COLORS.cyanGlow, 0.03);
    for (let y = 0; y < height; y += 3) {
      scanlines.lineBetween(0, y, width, y);
    }
    container.add(scanlines);
  }

  return container;
}

/**
 * Create an animated progress bar with glow
 */
export function createGlowingProgressBar(
  scene: Phaser.Scene,
  width: number,
  height: number,
  options: {
    trackColor?: number;
    fillColor?: number;
    glowColor?: number;
    hasParticles?: boolean;
  } = {}
): {
  container: Phaser.GameObjects.Container;
  graphics: Phaser.GameObjects.Graphics;
  setProgress: (progress: number) => void;
} {
  const {
    trackColor = COLORS.dataHidden,
    fillColor = COLORS.success,
    glowColor = COLORS.greenGlow,
  } = options;

  const container = scene.add.container(0, 0);
  const graphics = scene.add.graphics();
  container.add(graphics);

  let currentProgress = 0;

  const setProgress = (progress: number) => {
    currentProgress = Math.max(0, Math.min(1, progress));

    graphics.clear();

    // Track background
    graphics.fillStyle(trackColor, 0.6);
    graphics.fillRoundedRect(0, 0, width, height, height / 2);

    // Track inner shadow
    graphics.fillStyle(0x000000, 0.3);
    graphics.fillRoundedRect(1, 1, width - 2, height - 2, (height - 2) / 2);

    if (currentProgress > 0) {
      const fillWidth = Math.max(height, width * currentProgress);

      // Fill glow (outer)
      graphics.fillStyle(glowColor, 0.3);
      graphics.fillRoundedRect(-2, -2, fillWidth + 4, height + 4, (height + 4) / 2);

      // Main fill
      graphics.fillStyle(fillColor, 1);
      graphics.fillRoundedRect(0, 0, fillWidth, height, height / 2);

      // Highlight streak
      graphics.fillStyle(0xffffff, 0.3);
      graphics.fillRoundedRect(2, 2, fillWidth - 4, height * 0.35, height * 0.2);

      // Pulse effect at leading edge
      if (currentProgress < 1) {
        const pulseX = fillWidth - height / 2;
        graphics.fillStyle(0xffffff, 0.6);
        graphics.fillCircle(pulseX, height / 2, height / 3);
      }
    }

    // Track border
    graphics.lineStyle(1, COLORS.panelHighlight, 0.5);
    graphics.strokeRoundedRect(0, 0, width, height, height / 2);
  };

  setProgress(0);

  return { container, graphics, setProgress };
}

/**
 * Create a data row with label and value
 */
export function createDataRow(
  scene: Phaser.Scene,
  label: string,
  width: number,
  options: {
    labelColor?: string;
    valueColor?: string;
    fontSize?: number;
    fontFamily?: string;
    hasIndicator?: boolean;
    indicatorColor?: number;
  } = {}
): {
  container: Phaser.GameObjects.Container;
  labelText: Phaser.GameObjects.Text;
  valueText: Phaser.GameObjects.Text;
  indicator?: Phaser.GameObjects.Graphics;
  setValue: (value: string, revealed?: boolean) => void;
} {
  const {
    labelColor = '#94d2ff',
    valueColor = '#3a5a7a',
    fontSize = 11,
    fontFamily = '"JetBrains Mono", "Fira Code", "SF Mono", Consolas, monospace',
    hasIndicator = true,
    indicatorColor: _indicatorColor = COLORS.cyanGlow,
  } = options;

  const container = scene.add.container(0, 0);

  // Status indicator dot
  let indicator: Phaser.GameObjects.Graphics | undefined;
  if (hasIndicator) {
    indicator = scene.add.graphics();
    indicator.fillStyle(COLORS.dataHidden, 0.6);
    indicator.fillCircle(4, fontSize / 2 + 2, 3);
    container.add(indicator);
  }

  const labelX = hasIndicator ? 14 : 0;

  const labelText = scene.add.text(labelX, 0, label, {
    fontFamily,
    fontSize: `${fontSize}px`,
    color: labelColor,
  });
  container.add(labelText);

  const valueText = scene.add.text(width - 10, 0, '???', {
    fontFamily,
    fontSize: `${fontSize}px`,
    color: valueColor,
  });
  valueText.setOrigin(1, 0);
  container.add(valueText);

  const setValue = (value: string, revealed: boolean = true) => {
    valueText.setText(value);
    if (revealed) {
      valueText.setColor('#10b981');
      if (indicator) {
        indicator.clear();
        indicator.fillStyle(COLORS.success, 1);
        indicator.fillCircle(4, fontSize / 2 + 2, 3);
        // Glow
        indicator.fillStyle(COLORS.greenGlow, 0.3);
        indicator.fillCircle(4, fontSize / 2 + 2, 6);
      }
    } else {
      valueText.setColor(valueColor);
      if (indicator) {
        indicator.clear();
        indicator.fillStyle(COLORS.dataHidden, 0.6);
        indicator.fillCircle(4, fontSize / 2 + 2, 3);
      }
    }
  };

  return { container, labelText, valueText, indicator, setValue };
}

/**
 * Create a section title with decorative elements
 */
export function createSectionTitle(
  scene: Phaser.Scene,
  text: string,
  width: number,
  options: {
    icon?: string;
    color?: string;
    accentColor?: number;
    fontSize?: number;
  } = {}
): Phaser.GameObjects.Container {
  const {
    icon = '',
    color = '#00d4ff',
    accentColor = COLORS.cyanGlow,
    fontSize = 12,
  } = options;

  const container = scene.add.container(0, 0);

  // Left accent line
  const leftLine = scene.add.graphics();
  leftLine.lineStyle(2, accentColor, 0.6);
  leftLine.lineBetween(0, fontSize / 2 + 2, 8, fontSize / 2 + 2);
  // Glow dot at start
  leftLine.fillStyle(accentColor, 0.8);
  leftLine.fillCircle(0, fontSize / 2 + 2, 2);
  container.add(leftLine);

  // Title text
  const title = scene.add.text(14, 0, `${icon} ${text}`.trim(), {
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    fontSize: `${fontSize}px`,
    color,
    fontStyle: 'bold',
  });
  container.add(title);

  // Right accent line extending to width
  const rightLine = scene.add.graphics();
  const lineStartX = 18 + title.width;
  rightLine.lineStyle(1, accentColor, 0.3);
  rightLine.lineBetween(lineStartX, fontSize / 2 + 2, width - 5, fontSize / 2 + 2);
  container.add(rightLine);

  return container;
}

/**
 * Add breathing animation to a container (subtle scale pulse)
 */
export function addBreathingAnimation(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Container | Phaser.GameObjects.Graphics,
  options: {
    minScale?: number;
    maxScale?: number;
    duration?: number;
  } = {}
): Phaser.Tweens.Tween {
  const {
    minScale = 0.98,
    maxScale = 1.02,
    duration = TIMING.breathe,
  } = options;

  return scene.tweens.add({
    targets: target,
    scaleX: { from: minScale, to: maxScale },
    scaleY: { from: minScale, to: maxScale },
    duration,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });
}

/**
 * Add glow pulse animation
 */
export function addGlowPulse(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Graphics | Phaser.GameObjects.Container,
  options: {
    minAlpha?: number;
    maxAlpha?: number;
    duration?: number;
  } = {}
): Phaser.Tweens.Tween {
  const {
    minAlpha = 0.3,
    maxAlpha = 0.7,
    duration = 1500,
  } = options;

  return scene.tweens.add({
    targets: target,
    alpha: { from: minAlpha, to: maxAlpha },
    duration,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });
}

/**
 * Create reveal animation for data
 */
export function revealAnimation(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Text | Phaser.GameObjects.Container,
  options: {
    duration?: number;
    delay?: number;
    onComplete?: () => void;
  } = {}
): void {
  const { duration = TIMING.normal, delay = 0, onComplete } = options;

  const originalX = target.x;
  target.setAlpha(0);
  target.x = originalX - 10;

  scene.tweens.add({
    targets: target,
    alpha: 1,
    x: originalX,
    duration,
    delay,
    ease: 'Back.easeOut',
    onComplete,
  });
}
